import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";

export default function EnergyManagement() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("Energy Management")}
      title={t("Stop paying to make nothing.")}
      seoTitle="ISO 50001 Energy Management Software — BEEDIGIT"
      seoDescription="Live SEC per product, idle-energy detection, demand-peak control and ISO 50001 evidence — all anchored to real production. Book a demo."
      description={t("BEEDIGIT Energy puts a real number on every kWh: which product consumed it, which machine wasted it, and which shift could have avoided it. Stop reporting kWh totals nobody can act on — start cutting the energy bill where it actually leaks.")}
      bullets={[
        "Live SEC per product, batch and machine",
        "Spot machines burning power while producing nothing",
        "Catch demand peaks before they drive your tariff",
        "ISO 50001 evidence — generated, not assembled",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk to expert", to: "/get-started" }}
      ctaHeading="Find the kWh you're paying for and not using."
      ctaBody="Show us one line. In 30 minutes we'll show you the idle energy, the SEC drift and the peak charges you've been quietly paying for."
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If you sign the energy bill, this is for you",
          intro: "Energy data only matters when it's tied to production. BEEDIGIT puts every kWh against a product, a machine and a shift.",
          items: [
            { role: "CEO / Owner", pain: "Energy is one of my top 3 costs and I can't influence it.", value: "A clear lever on margin — without buying anything new." },
            { role: "COO", pain: "Sustainability targets are real, but the data isn't.", value: "Auditable energy data, normalized to production — across every plant." },
            { role: "Plant Manager", pain: "Bills go up. Nobody can tell me why.", value: "See which machine, which shift, which product is driving the cost." },
            { role: "Energy Manager", pain: "I'm running ISO 50001 on spreadsheets and meter walks.", value: "EnPIs, baselines and SEUs generated continuously — not assembled quarterly." },
            { role: "CFO", pain: "Energy cost moves and I can't tie it to anything operational.", value: "EUR per useful output, by line and by shift — finally a controllable KPI." },
            { role: "Sustainability Lead", pain: "Carbon reporting is built on estimates.", value: "Production-anchored kWh — real numbers for your ESG report." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "Your kWh bill doesn't tell you anything you can fix.",
          body:
            "Most factories know exactly how much energy they bought last month — and almost nothing about how they spent it. Totals roll up by site. Meters live in spreadsheets. Idle machines burn power overnight and nobody sees it. Peak charges spike the tariff and nobody can point to a cause.\n\nMeanwhile the cheapest kWh is the one you don't waste. It's already on your floor. You just can't see it.\n\nBEEDIGIT Energy ties every kWh to a product, a machine and a shift — so for the first time the energy bill becomes a list of decisions you can act on.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "Production-anchored energy in three steps",
          intro: "Non-invasive sensors. No machine downtime. Real SEC by the end of the first week.",
          items: [
            { n: 1, title: "We meter", body: "Non-invasive current transformers and HoneyPlug sensors clip on without modifying the equipment. No downtime, no panel work, no IT migration." },
            { n: 2, title: "We anchor", body: "Energy is tied live to the job order, product and batch each machine is running — so kWh become SEC, not just totals." },
            { n: 3, title: "You act", body: "Idle-energy alerts, demand-peak warnings and EnPI drifts flag themselves. Your team stops debating averages and starts cutting the bill." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "Energy data that finally makes sense",
          cols: 3,
          items: [
            { title: "SEC per product", desc: "kWh per kilogram, meter, piece or batch — whatever your useful output is." },
            { title: "Idle-energy detection", desc: "See machines burning power while producing nothing. Most plants find their first 5% in a week." },
            { title: "Demand-peak alerts", desc: "Catch the spikes that drive your tariff before they cost you the month." },
            { title: "EnPIs & baselines", desc: "Track and prove energy performance over time — automatically, not in Excel." },
            { title: "Significant Energy Uses", desc: "Identify and monitor your biggest consumers — ranked by what they actually cost." },
            { title: "ISO 50001 evidence", desc: "Documentation aligned to the standard's requirements — generated from real data." },
            { title: "Energy during downtime", desc: "Quantify what stopped machines still cost you in energy." },
            { title: "Carbon translation", desc: "kWh to CO₂e with auditable factors — ESG reporting from the same data." },
            { title: "Your data, your ERP", desc: "Push energy KPIs to SAP, Oracle, Dynamics, Odoo, Power BI — out of the box." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "First savings in 30 days",
          heading: "The cheapest kWh is the one you don't waste.",
          body: "You don't need to retrofit your panels. You don't need a 12-month meter project. We clip on non-invasive sensors, anchor energy to production, and you're typically cutting the first 5% inside 30 days.",
          href: "/hardware",
          linkLabel: "See the hardware",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What every role gets back",
          intro: "Energy stops being a number on a bill. It becomes a number on a screen — with someone accountable.",
          items: [
            { role: "CEO", cares: "Margin and resilience.", gain: "A controllable 5–15% on one of your top 3 cost lines.", kpis: "Energy cost / unit, energy as % of COGS.", pitch: "Move a top-3 cost line — without new equipment." },
            { role: "COO", cares: "Performance across plants.", gain: "Same SEC scorecard, every site, every shift.", kpis: "SEC by plant, idle-energy ratio, peak events.", pitch: "Benchmark every plant on the same number." },
            { role: "Plant Manager", cares: "Bill drivers.", gain: "Which machine, which shift, which product — ranked.", kpis: "SEC by line, idle kWh, peak demand.", pitch: "Know what's driving the bill — by Tuesday." },
            { role: "Energy Manager", cares: "ISO 50001.", gain: "EnPIs, baselines, SEUs generated continuously — audit-ready.", kpis: "EnPI drift, SEU coverage, action impact.", pitch: "Trade meter walks for management." },
            { role: "CFO", cares: "Tariff and budget.", gain: "Demand-peak alerts cut the part of the bill nobody else can.", kpis: "Demand charge, peak events, tariff variance.", pitch: "Stop discovering peak charges on the invoice." },
            { role: "Sustainability", cares: "ESG and carbon.", gain: "Production-anchored kWh and CO₂e — defensible numbers for your report.", kpis: "kWh / unit, tCO₂e, intensity by product.", pitch: "Report numbers you can actually defend." },
            { role: "Maintenance", cares: "Asset health.", gain: "Rising SEC on a machine is often the first sign of a failing one.", kpis: "SEC drift, abnormal load events.", pitch: "Catch wear before it becomes downtime." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The plant manager", scenario: "Sees Compressor 2 ran all night at 70% load — with the line stopped.", outcome: "One change to the shift routine. EUR 1.8k/month back." },
            { persona: "The energy manager", scenario: "Pulls last quarter's EnPI. Line 3 has drifted 9% above baseline.", outcome: "Targets the audit where it pays back — instead of everywhere." },
            { persona: "The CFO", scenario: "Tariff bill spikes. Demand-peak log shows two 15-min peaks on the same Tuesday.", outcome: "Schedule change avoids the next peak. Bill drops the following month." },
            { persona: "The sustainability lead", scenario: "Pulls kWh and CO₂e per product for the ESG report — straight from production data.", outcome: "Numbers signed off by audit. No spreadsheet gymnastics." },
            { persona: "The maintenance lead", scenario: "Gets an alert: Motor 4 SEC up 14% in two weeks.", outcome: "Plans a bearing change. Avoids the unplanned breakdown." },
            { persona: "The board", scenario: "Asks for the energy roadmap. Plant pulls up SEC trends, EnPIs and savings logged this year.", outcome: "Conversation moves from \"trust us\" to \"here's the data\"." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "Energy in the language of the factory",
          intro: "Not kWh on a meter. Cost per product, idle energy by line, peak alerts on the right phone.",
          items: [
            { alt: "SEC dashboard", caption: "kWh per useful output — by product, machine and shift. The number that finally makes energy actionable." },
            { alt: "Idle-energy heatmap", caption: "Where you're paying to make nothing. Usually the first 5% of savings live here." },
            { alt: "EnPI trend", caption: "Track baselines and improvements — ISO 50001 evidence, generated continuously." },
            { alt: "Demand-peak log", caption: "Every peak, ranked by cost. Schedule changes pay for themselves the next billing cycle." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs the energy bill will respond to",
          intro: "Live. Drillable to the machine. Exportable to your ERP and ESG report.",
          items: [
            { value: "SEC", label: "kWh per useful output" },
            { value: "Idle kWh", label: "Power burned doing nothing" },
            { value: "Peak", label: "Demand events that cost you" },
            { value: "EnPI", label: "Performance vs baseline" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT Energy",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT Energy",
          left: [
            "Monthly kWh totals nobody can act on",
            "Idle machines burning power overnight, invisibly",
            "Demand peaks discovered on the bill",
            "ISO 50001 evidence assembled in Excel",
            "Carbon reporting built on estimates",
            "Energy and production live in different worlds",
          ],
          right: [
            "SEC per product, batch and machine — live",
            "Idle-energy flagged the moment it starts",
            "Demand peaks alerted before they hit the bill",
            "EnPIs, baselines and SEUs generated continuously",
            "kWh and CO₂e tied to real production",
            "One data model — energy meets production",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "Non-invasive meters, panel-friendly install",
          intro: "Clip-on current transformers, panel-level metering, no machine downtime to install.",
          items: [
            "BEEDIGIT HUB",
            "HoneyPlug current sensor",
            "BEECT current transformer",
            "Pulse / 4-20 mA inputs",
            "GSM · Wi-Fi · Ethernet",
            "Encrypted MQTT over TLS",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your BI, your ESG report",
          intro: "Push live energy KPIs into the tools your business already runs on.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Power BI", "Tableau", "Data lakes (CSV / JSON / REST)",
            "MQTT", "OPC UA", "REST / Webhooks",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From totals to action in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "Walk the plant — identify SEUs and biggest consumers",
            "Install non-invasive meters on first 3 SEUs",
            "First SEC baseline by product and machine",
            "First idle-energy and peak-event findings — usually the first 5%",
          ],
          right: [
            "Roll out metering across remaining SEUs",
            "EnPIs and baselines tuned with your energy team",
            "ISO 50001 evidence pack generated continuously",
            "Savings tracked and reported — to the board, not the binder",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Energy and sustainability score in SIRI. BEEDIGIT is the first platform officially recognized for moving the needle on the Smart Industry Readiness Index — and Energy Management is a core driver.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            { q: "Do we need to retrofit our electrical panels?", a: "No. We use non-invasive current transformers and clip-on sensors. No machine downtime, no panel rework, no electrician project." },
            { q: "How fast do we see savings?", a: "Most customers identify the first 5% of savings (typically idle energy and demand peaks) within the first 30 days of live metering on the priority SEUs." },
            { q: "Will this make us ISO 50001 compliant?", a: "BEEDIGIT generates the operational evidence — EnPIs, baselines, SEU monitoring, action tracking — that ISO 50001 requires. Certification still goes through your auditor, but the evidence pack is ready." },
            { q: "Can we trust the kWh-to-CO₂e numbers for ESG?", a: "Yes. We apply auditable emission factors (regional grid, fuel-specific) and tie every kWh to a real production event. Your auditor can trace any number back to source." },
            { q: "Does it work without production data?", a: "It works as a pure energy meter, but the value multiplies when energy is anchored to production. Most customers add Monitoring/OEE alongside Energy for exactly this reason." },
            { q: "What about gas, steam, compressed air, water?", a: "Yes — we support pulse, 4-20 mA and Modbus meters, so any utility with a meter can be brought into the same SEC and EnPI framework." },
            { q: "Will it connect to our ERP and BI?", a: "Out of the box — SAP, Oracle, Microsoft Dynamics, Odoo, Power BI, Tableau. Bi-directional REST/JSON, plus MQTT and OPC UA." },
            { q: "What does it cost?", a: "Priced per metered SEU, hardware included. Most customers see payback inside two billing cycles. Book the demo — we'll build the business case on your tariff." },
          ],
        },
        {
          kind: "related",
          eyebrow: "Explore the platform",
          heading: "Pairs naturally with",
          items: [
            { title: "Monitoring & OEE", href: "/products/monitoring-oee", desc: "Anchor energy to real production — kWh per useful output, not per shift." },
            { title: "Manufacturing Execution", href: "/products/manufacturing-execution", desc: "Job orders and confirmations on a tablet — the production context for every kWh." },
            { title: "CMMS", href: "/products/cmms", desc: "Rising SEC is often early warning of asset wear — feed maintenance directly." },
            { title: "Workspace", href: "/products/workspace", desc: "Andon and issue tracking for energy events — close the loop on every alert." },
          ],
        },
      ]}
    />
  );
}